import { useMemo, useCallback } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { ValueOf } from 'sl-api-connector';
import {
  useAppSelector,
  useComparisonPeriod,
  ComparisonPeriod,
  useMetricFormatter,
  useWeekRange,
  WeekRange
} from 'src/utils/Hooks';
import { getAppName, calculatePercentChange } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  getFirstWeekIdOfYear,
  getPreviousWeekId,
  weekIdToTimestamp,
  yearPartOfWeekId,
  getPreviousYearWeekId
} from 'src/utils/dateUtils';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import type { TrafficSummaryResponse } from './useDorisMetricByWeekId';

export interface UseDorisMetricByWeekIdBypassFiltersArgs {
  queryKey: string;
  metricType: string;
  methodName?: string;
  resultCount?: number;
  metricTypeDisplay?: ValueOf<typeof METRICTYPE>;
  useLatestWeek?: boolean;
  skipComparison?: boolean;
  metricField?: string;
  baseParamsBuilder?: (params: Record<string, unknown>) => Record<string, unknown>;
  allowedFilterKeys?: Array<
    | 'categoryIds'
    | 'subcategoryIds'
    | 'brandIds'
    | 'segmentIds'
    | 'businessunitids'
    | 'minRetailPrice'
    | 'maxRetailPrice'
    | 'keywords'
    | 'topicIds'
    | 'trendIds'
    | 'branding'
  >;
  overrideStartWeekId?: number;
  overrideEndWeekId?: number;
}

const KNOWN_FILTER_KEYS = [
  'categoryIds',
  'subcategoryIds',
  'brandIds',
  'segmentIds',
  'businessunitids',
  'minRetailPrice',
  'maxRetailPrice',
  'keywords',
  'topicIds',
  'trendIds',
  'branding'
] as const;

const getSplineDataFromResponse = (
  response: TrafficSummaryResponse | undefined,
  weekIds: number[],
  metricField: string
): number[][] => {
  if (!response) return [];

  if (response.records && Array.isArray(response.records)) {
    return response.records.sort((a, b) => a.weekId - b.weekId).map((r) => [weekIdToTimestamp(r.weekId), r.value || 0]);
  }

  const metricValue = response[metricField] || response[response.metricType] || 0;
  const valuePerWeek = weekIds.length > 0 ? metricValue / weekIds.length : 0;
  return weekIds.map((weekId) => [weekIdToTimestamp(weekId), valuePerWeek]);
};

export const useDorisMetricByWeekIdBypassFilters = ({
  queryKey,
  metricType,
  methodName = 'keymetrics',
  resultCount = 1200,
  metricTypeDisplay,
  useLatestWeek = false,
  skipComparison = false,
  metricField,
  baseParamsBuilder,
  allowedFilterKeys,
  overrideStartWeekId,
  overrideEndWeekId
}: UseDorisMetricByWeekIdBypassFiltersArgs) => {
  const formatMetric = useMetricFormatter();
  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();
  const retailerId = useRetailerId();

  const metricFieldToUse = useMemo(() => metricField || metricType, [metricField, metricType]);

  const mainStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const mainEndWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);

  const defaultStartWeekId = overrideStartWeekId ?? mainStartWeekId;
  const endWeekId = overrideEndWeekId ?? mainEndWeekId;

  const isLastWeekWithPriorYear = useMemo(() => {
    return weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;
  }, [comparisonPeriod, weekRange]);

  const startWeekId = useMemo(() => {
    if (useLatestWeek) return endWeekId;
    return isLastWeekWithPriorYear ? getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId)) : defaultStartWeekId;
  }, [defaultStartWeekId, endWeekId, isLastWeekWithPriorYear, useLatestWeek]);

  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  const comparisonStartWeekId = useMemo(
    () => (isLastWeekWithPriorYear ? getPreviousYearWeekId(startWeekId) : defaultComparisonStartWeekId),
    [defaultComparisonStartWeekId, isLastWeekWithPriorYear, startWeekId]
  );

  const comparisonEndWeekId = useMemo(
    () => (isLastWeekWithPriorYear ? getPreviousWeekId(startWeekId) : defaultComparisonEndWeekId),
    [defaultComparisonEndWeekId, isLastWeekWithPriorYear, startWeekId]
  );

  const weekIdsInRange = useMemo(() => {
    const result: number[] = [];
    let current = startWeekId;
    while (current <= endWeekId) {
      result.push(current);
      if (current % 100 === 52) {
        current = (Math.floor(current / 100) + 1) * 100 + 1;
      } else {
        current += 1;
      }
    }
    return result;
  }, [startWeekId, endWeekId]);

  const comparisonWeekIdsInRange = useMemo(() => {
    if (skipComparison) return [];
    const result: number[] = [];
    let current = comparisonStartWeekId;
    while (current <= comparisonEndWeekId) {
      result.push(current);
      if (current % 100 === 52) {
        current = (Math.floor(current / 100) + 1) * 100 + 1;
      } else {
        current += 1;
      }
    }
    return result;
  }, [comparisonStartWeekId, comparisonEndWeekId, skipComparison]);

  const baseParams = useMemo(() => {
    const initial = { metricType, retailerId: +retailerId } as Record<string, unknown>;
    const withBuilder = baseParamsBuilder ? baseParamsBuilder(initial) : initial;

    if (!allowedFilterKeys || allowedFilterKeys.length === 0) {
      return withBuilder;
    }
    const allowed = new Set(allowedFilterKeys);
    const filtered: Record<string, unknown> = { metricType, retailerId: +retailerId };
    for (const key of allowedFilterKeys) {
      if (withBuilder[key] !== undefined) filtered[key] = withBuilder[key];
    }
    // Ensure we don't accidentally carry disallowed keys forward
    for (const key of KNOWN_FILTER_KEYS) {
      if (!allowed.has(key)) delete (filtered as any)[key];
    }
    return filtered;
  }, [metricType, retailerId, baseParamsBuilder, allowedFilterKeys]);

  const buildPayload = useMemo(
    () => (start: number, end: number) => {
      const payload: Record<string, unknown> = {
        ...baseParams,
        resultCount,
        startWeekId: start,
        endWeekId: end
      };
      if (allowedFilterKeys && allowedFilterKeys.length > 0) {
        const allowed = new Set(allowedFilterKeys);
        for (const key of KNOWN_FILTER_KEYS) {
          if (!allowed.has(key)) delete (payload as any)[key];
        }
      }
      return payload;
    },
    [baseParams, resultCount, allowedFilterKeys]
  );

  const constructUrl = useMemo(
    () =>
      (isComparison = false) => {
        const idSuffix = isComparison ? '_comparison' : '';
        return `api/${getAppName()}/${methodName}?id=${metricType}_${startWeekId}_${endWeekId}${idSuffix}`;
      },
    [methodName, metricType, startWeekId, endWeekId]
  );

  const { data: primaryData, isLoading: isPrimaryLoading } = useQuery<TrafficSummaryResponse>({
    queryKey: [queryKey, methodName, metricType, 'primary', baseParams, startWeekId, endWeekId],
    queryFn: async () => {
      const response = await axios.post(constructUrl(false), buildPayload(startWeekId, endWeekId));
      return response.data;
    },
    enabled: !!retailerId
  });

  const { data: comparisonData, isLoading: isComparisonLoading } = useQuery<TrafficSummaryResponse>({
    queryKey: [queryKey, methodName, metricType, 'comparison', baseParams, comparisonStartWeekId, comparisonEndWeekId],
    queryFn: async () => {
      const response = await axios.post(constructUrl(true), buildPayload(comparisonStartWeekId, comparisonEndWeekId));
      return response.data;
    },
    enabled: !!retailerId && !skipComparison
  });

  const primData = useMemo(
    () => getSplineDataFromResponse(primaryData, weekIdsInRange, metricFieldToUse),
    [primaryData, weekIdsInRange, metricFieldToUse]
  );

  const compData = useMemo(() => {
    if (skipComparison || !comparisonData) return [] as number[][];
    return getSplineDataFromResponse(comparisonData, comparisonWeekIdsInRange, metricFieldToUse);
  }, [skipComparison, comparisonData, comparisonWeekIdsInRange, metricFieldToUse]);

  const getMetricTotal = useCallback(
    (response: TrafficSummaryResponse | undefined, isComparison = false) => {
      if (!response) return 0;

      const shouldFilterByCorrespondingWeeks =
        isComparison && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR && weekRange === WeekRange.YTD;

      if (response.records && Array.isArray(response.records)) {
        const prevYearWeekIds = shouldFilterByCorrespondingWeeks
          ? weekIdsInRange.map((weekId) => getPreviousYearWeekId(weekId))
          : null;

        return response.records
          .filter(
            (record) =>
              !shouldFilterByCorrespondingWeeks || (prevYearWeekIds && prevYearWeekIds.includes(record.weekId))
          )
          .reduce((sum, r) => sum + (r.value || 0), 0);
      }

      return (response as any)[metricFieldToUse] || (response as any)[metricType] || 0;
    },
    [comparisonPeriod, weekRange, weekIdsInRange, metricFieldToUse, metricType]
  );

  const primaryTotal = useMemo(() => getMetricTotal(primaryData), [getMetricTotal, primaryData]);

  const secondaryTotal = useMemo(
    () => (skipComparison ? 0 : getMetricTotal(comparisonData, true)),
    [skipComparison, getMetricTotal, comparisonData]
  );

  const metricTypeToUse = metricTypeDisplay || METRICTYPE.VOLUME;

  const percentChange = useMemo(() => {
    if (skipComparison) return 0;
    return calculatePercentChange(primaryTotal, secondaryTotal);
  }, [skipComparison, primaryTotal, secondaryTotal]);

  const isLoading = isPrimaryLoading || (!skipComparison && isComparisonLoading);

  return useMemo(
    () => ({
      primaryData:
        !skipComparison && comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && compData.length > 0
          ? [compData[compData.length - 1], ...primData]
          : primData,
      secondaryData: compData,
      primarySlicedData: primData,
      secondarySlicedData: compData,
      mainRowsWithWeekId: primData,
      comparisonRowsWithWeekId: compData,
      primaryTotal,
      secondaryTotal,
      formattedPrimaryTotal: formatMetric(primaryTotal, metricTypeToUse, {
        showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
        decimalPlaces: 2,
        showNegative: true
      }),
      formattedPercentChange: formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, {
        decimalPlaces: 2
      }),
      percentChange,
      isLoading
    }),
    [
      primData,
      compData,
      primaryTotal,
      secondaryTotal,
      formatMetric,
      metricTypeToUse,
      percentChange,
      isLoading,
      skipComparison,
      comparisonPeriod
    ]
  );
};

export default useDorisMetricByWeekIdBypassFilters;
